import { Plus, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import type { ParamMapping } from '@/types/integration-form-types';

interface ParameterMappingPanelProps {
    value: ParamMapping[];
    onChange: (params: ParamMapping[]) => void;
    keyPlaceholder?: string;
    disabled?: boolean;
}

export function ParameterMappingPanel({
    value,
    onChange,
    keyPlaceholder = 'Parameter key',
    disabled = false,
}: ParameterMappingPanelProps) {
    const handleAdd = () => {
        onChange([...value, { key: '', valueExpression: '' }]);
    };

    const handleRemove = (index: number) => {
        onChange(value.filter((_, i) => i !== index));
    };

    const handleChange = (index: number, field: 'key' | 'valueExpression', val: string) => {
        const updated = value.map((item, i) =>
            i === index ? { ...item, [field]: val } : item,
        );
        onChange(updated);
    };

    return (
        <div className="space-y-2">
            {value.length > 0 && (
                <div className="space-y-2">
                    <div className="grid grid-cols-[1fr_1fr_36px] gap-2 text-xs text-slate-400 font-medium px-1">
                        <span>Key</span>
                        <span>Value Expression (JSONPath)</span>
                        <span />
                    </div>
                    {value.map((param, index) => (
                        <div key={index} className="grid grid-cols-[1fr_1fr_36px] gap-2 items-center">
                            <Input
                                placeholder={keyPlaceholder}
                                value={param.key}
                                onChange={(e) => handleChange(index, 'key', e.target.value)}
                                className="font-mono text-sm"
                                disabled={disabled}
                            />
                            <Input
                                placeholder="$.input.fieldName"
                                value={param.valueExpression}
                                onChange={(e) => handleChange(index, 'valueExpression', e.target.value)}
                                className="font-mono text-sm"
                                disabled={disabled}
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemove(index)}
                                disabled={disabled}
                                className="h-9 w-9 p-0 text-slate-400 hover:text-red-500"
                            >
                                <Trash2 size={14} />
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            <Button
                type="button"
                size="sm"
                onClick={handleAdd}
                disabled={disabled}
                className="gap-1 text-xs bg-btn-primary text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <Plus size={12} />
                Add Parameter
            </Button>
        </div>
    );
}
